import React, { useId } from 'react';

interface InputFieldProps {
    value: number;
    onChange: (value: number) => void;
    placeholder: string;
    label: string;
    infoText?: string;
    min?: number;
    max?: number;
    errorMessage?: string;
    showValidation?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
    value,
    onChange,
    placeholder,
    label,
    infoText,
    min,
    max,
    errorMessage,
    showValidation = false
}) => {
    const inputId = useId();
    const errorId = `${inputId}-error`;
    const infoId = `${inputId}-info`;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Only allow numbers
        const numericValue = e.target.value.replace(/[^0-9]/g, '');
        const numValue = parseInt(numericValue) || 0;

        // Don't limit the value - let user enter any number
        // Validation will show error if out of range
        onChange(numValue);
    };

    // Check if value is out of range or empty when it should have a value
    const isOutOfRange = value > 0 && ((min && value < min) || (max && value > max));
    const isEmpty = value === 0;
    const hasError = isOutOfRange || (isEmpty && min && min > 0);
    const showError = showValidation && hasError;
    const isRequired = min !== undefined && min > 0;

    const handleBlur = () => {
        // No default value - let user enter their own value
    };

    return (
        <div className="form-group">
            <div className="input-header">
                {infoText && (
                    <>
                        <span className="info-text" id={infoId}>{infoText}</span>
                        <div className="info-dot" aria-hidden="true"></div>
                    </>
                )}
                <label className="form-label" htmlFor={inputId}>
                    {label}
                    {isRequired && <span aria-label="נדרש"> *</span>}
                </label>
            </div>
            <div className="input-field">
                <input
                    id={inputId}
                    type="text"
                    inputMode="numeric"
                    value={value === 0 ? '' : value}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    className={showError ? 'error' : ''}
                    style={showError ? { color: 'red', borderColor: 'red' } : {}}
                    aria-required={isRequired}
                    aria-invalid={showError ? true : false}
                    aria-describedby={`${showError ? errorId : ''} ${infoText ? infoId : ''}`.trim() || undefined}
                />
            </div>
            {showError && errorMessage && (
                <div className="error-message" id={errorId} role="alert">
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default InputField;
